const OVERLAY_ID = "print-preview-overlay";
const STYLE_ID = "print-preview-styles";
const PRINTABLE_ID = "print-preview-printable";
const DEFAULT_TITLE = "Print Preview";
const PRINT_TIMEOUT_MS = 60000;

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const ensurePreviewStyles = () => {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(STYLE_ID)) {
    return;
  }
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.innerHTML = `
    .print-preview-overlay {
      position: fixed;
      inset: 0;
      background: rgba(4, 7, 26, 0.86);
      z-index: 1300;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 32px;
      box-sizing: border-box;
    }

    .print-preview-dialog {
      width: min(1200px, 100%);
      height: min(100%, 960px);
      background: #f3f4f6;
      border-radius: 20px;
      box-shadow: 0 25px 60px rgba(15, 23, 42, 0.45);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid rgba(148, 163, 184, 0.4);
    }

    .print-preview-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 28px;
      background: #111827;
      color: #fff;
    }

    .print-preview-title {
      font-size: 18px;
      font-weight: 600;
    }

    .print-preview-subtitle {
      margin: 6px 0 0;
      opacity: 0.7;
      font-size: 13px;
    }

    .print-preview-actions {
      display: flex;
      gap: 12px;
    }

    .print-preview-button {
      border: none;
      border-radius: 999px;
      padding: 10px 22px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s ease;
    }

    .print-preview-button:hover {
      opacity: 0.9;
    }

    .print-preview-button.print-preview-secondary {
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
    }

    .print-preview-button.print-preview-primary {
      background: #facc15;
      color: #1f2937;
    }

    .print-preview-scroll {
      flex: 1;
      padding: 28px;
      overflow: auto;
      background: #e5e7eb;
    }

    #${PRINTABLE_ID} {
      margin: 0 auto;
      width: min(1024px, 100%);
      background: #fff;
      border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 10px 35px rgba(15, 23, 42, 0.25);
    }
    #${PRINTABLE_ID},
    #${PRINTABLE_ID} * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body.print-preview-open {
      overflow: hidden;
    }

    @media print {
      body.print-preview-printing * {
        visibility: hidden !important;
      }

      body.print-preview-printing > *:not(.print-preview-overlay) {
        display: none !important;
      }

      body.print-preview-printing #${PRINTABLE_ID},
      body.print-preview-printing #${PRINTABLE_ID} * {
        visibility: visible !important;
      }

      body.print-preview-printing .print-preview-toolbar,
      body.print-preview-printing .print-preview-actions,
      body.print-preview-printing .print-preview-overlay {
        visibility: visible !important;
      }

      body.print-preview-printing .print-preview-overlay {
        position: static !important;
        background: transparent !important;
        padding: 0 !important;
      }

      body.print-preview-printing .print-preview-dialog {
        width: auto !important;
        height: auto !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        border: none !important;
      }

      body.print-preview-printing .print-preview-scroll {
        padding: 0 !important;
        background: transparent !important;
        overflow: visible !important;
      }

      body.print-preview-printing .print-preview-toolbar,
      body.print-preview-printing .print-preview-actions {
        display: none !important;
      }
    }
  `;
  document.head.appendChild(style);
};

const removeOverlayIfExists = () => {
  if (typeof document === "undefined") {
    return;
  }
  const existing = document.getElementById(OVERLAY_ID);
  if (existing && existing.parentNode) {
    existing.parentNode.removeChild(existing);
  }
  document.body?.classList.remove("print-preview-open", "print-preview-printing");
};

const createOverlay = ({ title, contentHtml }) => {
  if (typeof document === "undefined") {
    return null;
  }

  ensurePreviewStyles();
  removeOverlayIfExists();

  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.className = "print-preview-overlay";
  overlay.innerHTML = `
    <div class="print-preview-dialog" role="dialog" aria-modal="true" aria-label="${escapeHtml(
      title
    )}">
      <div class="print-preview-toolbar">
        <div>
          <div class="print-preview-title">${escapeHtml(title)}</div>
          <div class="print-preview-subtitle">
            Review the preview. Click “Download PDF” to open the browser print dialog.
          </div>
        </div>
        <div class="print-preview-actions">
          <button class="print-preview-button print-preview-secondary" type="button" data-preview-action="cancel">
            Cancel
          </button>
          <button class="print-preview-button print-preview-primary" type="button" data-preview-action="print">
            Download PDF
          </button>
        </div>
      </div>
      <div class="print-preview-scroll">
        <div id="${PRINTABLE_ID}">
          ${contentHtml || ""}
        </div>
      </div>
    </div>
  `;

  document.body?.appendChild(overlay);
  document.body?.classList.add("print-preview-open");
  return overlay;
};

const destroyOverlay = () => {
  removeOverlayIfExists();
};

export const openPrintPreviewFromTemplate = (templateId, options = {}) =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(new Error("Print preview is only available in the browser."));
      return;
    }

    const templateNode = document.getElementById(templateId);
    if (!templateNode) {
      reject(new Error("Preview content is not available. Please try again."));
      return;
    }

    const overlay = createOverlay({
      title: options?.title || DEFAULT_TITLE,
      contentHtml: templateNode.innerHTML,
    });

    if (!overlay) {
      reject(new Error("Unable to render preview. Please try again."));
      return;
    }

    const printButton = overlay.querySelector('[data-preview-action="print"]');
    const cancelButton = overlay.querySelector('[data-preview-action="cancel"]');
    const printableArea = overlay.querySelector(`#${PRINTABLE_ID}`);

    if (!printableArea) {
      destroyOverlay();
      reject(new Error("Preview content failed to load."));
      return;
    }

    let resolved = false;
    let printTimer = null;

    const cleanup = (shouldResolve = true) => {
      if (resolved) {
        return;
      }
      resolved = true;
      if (printTimer) {
        clearTimeout(printTimer);
        printTimer = null;
      }
      window.removeEventListener("afterprint", handleAfterPrint);
      document.removeEventListener("keydown", handleKeydown);
      destroyOverlay();
      if (shouldResolve) {
        resolve();
      }
    };

    const handleAfterPrint = () => {
      document.body?.classList.remove("print-preview-printing");
      cleanup(true);
    };

    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        cleanup(false);
        reject(new Error("Print preview was closed."));
      }
    };

    const handleCancel = () => {
      cleanup(false);
      reject(new Error("Print preview was closed."));
    };

    const triggerPrint = () => {
      document.body?.classList.add("print-preview-printing");
      window.addEventListener("afterprint", handleAfterPrint, { once: true });
      printTimer = setTimeout(() => {
        document.body?.classList.remove("print-preview-printing");
        cleanup(true);
      }, PRINT_TIMEOUT_MS);
      setTimeout(() => {
        try {
          window.print();
        } catch (error) {
          cleanup(false);
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      }, 50);
    };

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) {
        handleCancel();
      }
    });

    document.addEventListener("keydown", handleKeydown);
    cancelButton?.addEventListener("click", handleCancel);
    printButton?.addEventListener("click", triggerPrint);
  });
